import { useEffect } from 'react';

import {
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BlockIcon from '@material-ui/icons/Block';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import { BlockedContactName } from 'components/BlockedContactName';
import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchAtendimentosIniciados,
  fetchFilaV2,
  fetchMarcarAtendimentoComoLido,
  iniciarContato,
  setAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import {
  canaisSelector,
  fetchCanaisSelect,
  resetCanaisSelect,
  TipoCanalEnum,
} from 'store/reducers/canais';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  usuariosSelector,
  fetchMinhasEquipes,
  resetMinhasEquipes,
} from 'store/reducers/usuarios';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import { getUrlParams } from 'utils/getUrlParams';
import * as validators from 'utils/validators';

type ModalIniciarContatoProps = {
  open: boolean;
  contato?: ContatoInterface;
  onClose: () => void;
  onSuccess?: () => void;
  onChangeContatoSelected?: () => void;
};

const useStyles = makeStyles(({ spacing, palette }) => ({
  textField: {
    marginTop: spacing(2),
  },
  truncateValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    marginRight: spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
  truncateText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  menuItem: {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  blockedAlert: {
    backgroundColor: alpha(palette.error.main, 0.12),
    border: `1px solid ${palette.error.main}`,
    borderRadius: spacing(1),
    padding: spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    marginBottom: spacing(1),
  },
  listItemDisabled: {
    // sem efeito de disabled
    color: 'red',
    opacity: 1,
    transform: 'none',
  },
}));

export default function ModalIniciarContato(props: ModalIniciarContatoProps) {
  const { open, onClose, onSuccess, onChangeContatoSelected, contato } = props;
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { canaisSelect, loadingCanaisSelect } = useAppSelector(canaisSelector);
  const { minhasEquipes, loadingMinhasEquipes } =
    useAppSelector(usuariosSelector);

  const { orderBy, orderInitiatedBy } = getUrlParams();

  const isBlocked = Boolean(contato?.blockedAt);

  const isInstagramOrMessengerChannel =
    contato?.tipoCanal === TipoCanalEnum.INSTAGRAM ||
    contato?.tipoCanal === TipoCanalEnum.MESSENGER;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      canal: '',
      equipe: minhasEquipes.length === 1 ? minhasEquipes[0]?.id : '',
    },
    validationSchema: validators.iniciarContatoSchema,
    onSubmit: async values => {
      if (!contato || !contato.id || isBlocked) return;

      const data = {
        contatoId: contato.id,
        canalId: values.canal,
        equipeId: values.equipe,
      };

      /**
       * Processos cruciais realizados após iniciar um novo contato
       *
       * 1º Atualiza a fila de espera
       * 2º Atualiza a lista de atendimentos iniciados
       * 3º Seleciona o atendimento iniciado para atendimento
       * 4º Envia o atendente para a página do Chat
       * 5º Confirma a leitura e zera o contador das mensagens nao lidas do atendimento iniciado
       */
      await dispatch(iniciarContato(data))
        .unwrap()
        .then(atendimentoIniciado => {
          dispatch(fetchFilaV2({ orderBy }));
          dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }));
          dispatch(setAtendimentoSelecionado(atendimentoIniciado));

          const atendimentoId = atendimentoIniciado?.id;
          if (atendimentoId) {
            dispatch(fetchMarcarAtendimentoComoLido(atendimentoId));
          }

          history.push('/');

          if (onSuccess) onSuccess();

          onClose();
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const {
    values,
    setFieldValue,
    handleSubmit,
    handleChange,
    resetForm,
    touched,
    errors,
    isSubmitting,
  } = formik;

  const handleClose = () => {
    if (onClose && !isSubmitting) onClose();
  };

  // Auto seleciona a equipe caso tenha apenas uma
  useEffect(() => {
    if (open && minhasEquipes.length === 1 && !values.equipe) {
      setFieldValue('equipe', minhasEquipes[0].id);
    }
  }, [open, minhasEquipes, setFieldValue, values.equipe]);

  // Busca os canais sempre que a equipe ou o contato mudar
  useEffect(() => {
    if (!open) return;

    const contatoId = contato?.id;
    const equipeId = values.equipe;

    if (contatoId && equipeId) {
      dispatch(
        fetchCanaisSelect({
          contactId: contatoId,
          equipeId,
        }),
      );
    }
  }, [contato?.id, values.equipe, dispatch, open]);

  // Auto seleciona o canal caso tenha apenas um ou um canal padrão
  useEffect(() => {
    if (canaisSelect.length === 0) {
      setFieldValue('canal', '');
      return;
    }

    if (canaisSelect.length === 1) {
      setFieldValue('canal', canaisSelect[0].id);
    } else {
      const defaultCanal = canaisSelect.find(c => c.isDefault);
      if (defaultCanal) {
        setFieldValue('canal', defaultCanal.id);
      }
    }
  }, [canaisSelect, setFieldValue]);

  const canalLabel =
    canaisSelect.length > 0 ? 'Canal' : 'Nenhum canal disponível';

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      TransitionProps={{
        onEnter: () => dispatch(fetchMinhasEquipes()),
        onExited: () => {
          resetForm();
          dispatch(resetCanaisSelect());
          dispatch(resetMinhasEquipes());
        },
      }}
    >
      <DialogHeader
        title="Iniciar contato"
        description="Agora selecione o canal e a equipe para dar início."
        onClose={handleClose}
      />

      <Box ml={3} mr={3}>
        {isBlocked && (
          <Box className={classes.blockedAlert}>
            <BlockIcon
              style={{
                color: theme.palette.error.main,
                fontSize: 16,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              style={{
                color: theme.palette.error.main,
                fontWeight: 500,
                fontSize: 11,
              }}
            >
              Contato bloqueado. Não é possível enviar mensagens.
            </Typography>
          </Box>
        )}

        <ListItem
          button
          selected
          disabled={!onChangeContatoSelected}
          onClick={() => {
            if (onChangeContatoSelected) onChangeContatoSelected();
          }}
          style={{
            opacity: 1,
          }}
        >
          <ListItemAvatar>
            <Avatar src={contato?.foto || ''} />
          </ListItemAvatar>

          <ListItemText
            primary={
              <BlockedContactName
                name={contato?.nome ?? ''}
                isBlocked={isBlocked}
              />
            }
            secondary={
              isInstagramOrMessengerChannel
                ? `Canal: ${contato?.canal?.nome}`
                : contato?.contato
            }
            primaryTypographyProps={
              {
                noWrap: true,
                variant: 'body2',
                component: 'div',
              } as Record<string, unknown>
            }
            secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
          />
        </ListItem>
      </Box>

      <DialogForm onSubmit={handleSubmit} loading={isSubmitting}>
        <DialogContent>
          <TextField
            select
            fullWidth
            id="equipe"
            name="equipe"
            variant="outlined"
            label={
              minhasEquipes.length > 0 ? 'Equipe' : 'Nenhuma equipe disponível'
            }
            disabled={
              isBlocked || loadingMinhasEquipes || minhasEquipes.length <= 0
            }
            className={classes.textField}
            value={values.equipe}
            onChange={e => {
              setFieldValue('canal', '');
              handleChange(e);
            }}
            error={touched.equipe && Boolean(errors.equipe)}
            helperText={touched.equipe && errors.equipe}
            InputProps={{
              startAdornment: loadingMinhasEquipes && (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ),
            }}
          >
            {minhasEquipes.map(equipe => (
              <MenuItem key={equipe.id} value={equipe.id}>
                <Typography variant="body2">{equipe.nome}</Typography>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            id="canal"
            name="canal"
            variant="outlined"
            label={canalLabel}
            disabled={
              isBlocked ||
              loadingCanaisSelect ||
              canaisSelect.length <= 0 ||
              isInstagramOrMessengerChannel ||
              !values.equipe
            }
            className={classes.textField}
            value={values.canal}
            onChange={handleChange}
            error={touched.canal && Boolean(errors.canal)}
            helperText={touched.canal && errors.canal}
            InputProps={{
              startAdornment: loadingCanaisSelect && (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ),
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: selected => {
                if ((selected as string) === '') {
                  return null;
                }
                const canal = canaisSelect.find(c => c.id === selected);
                return canal ? (
                  <Box className={classes.truncateValue}>
                    <span style={{ flexShrink: 0 }}>
                      {getIconTipoCanal({
                        tipoCanal: canal.type,
                        size: 16,
                        isModoHibrido: canal.isHybridMode,
                      })}
                    </span>
                    <span className={classes.truncateText}>{canal.name}</span>
                  </Box>
                ) : (
                  selected
                );
              },
            }}
          >
            {canaisSelect.map(canal => (
              <MenuItem
                key={canal.id}
                value={canal.id}
                className={classes.menuItem}
              >
                {getIconTipoCanal({
                  tipoCanal: canal.type,
                  size: 16,
                  isModoHibrido: canal.isHybridMode,
                })}
                <span style={{ paddingTop: 2 }}>{canal.name}</span>
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={isBlocked}
          >
            Iniciar
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}
